import { selectTenant } from '@saaga/shared/store/slices/billing.slice'
import { useAppSelector } from '@saaga/shared/store/store'
import { microsToDollars } from '@saaga/shared/utils/billing.util'
import { useCallback, useEffect, useState } from 'react'

import type {
  BillingAutoTopUpSettings,
  BillingBalance,
  BillingInterval,
  BillingPlan,
} from '@/components/billing/billing.interface'
import { useBillingClient } from '@/hooks/billing/use-billing-client.hook'
import { usePaymentMethodCheckout } from '@/hooks/billing/use-payment-method-checkout.hook'
import { useRedeemCode } from '@/hooks/billing/use-redeem-code.hook'
import { useSubscription } from '@/hooks/billing/use-subscription.hook'
import { useSubscriptionState } from '@/hooks/billing/use-subscription-state.hook'
import { useTopUp } from '@/hooks/billing/use-top-up.hook'
import { useOrganizationEarlyAccess } from '@/hooks/use-organization-early-access.hook'

import { useAutoTopUpState } from './use-auto-top-up-state.hook'

const DEFAULT_CURRENCY = 'USD'

const deriveBalance = (tenant: ReturnType<typeof selectTenant>): BillingBalance => ({
  amount: tenant ? microsToDollars(tenant.balance) : 0,
  currency: DEFAULT_CURRENCY,
})

export interface UseBalanceSettingsReturn {
  balance: BillingBalance
  hasPaymentMethod: boolean
  isTopUpModalOpen: boolean
  setIsTopUpModalOpen: (isOpen: boolean) => void
  isTopUpLoading: boolean
  isRedeemLoading: boolean
  isAddingPaymentMethod: boolean
  autoTopUpSettings: BillingAutoTopUpSettings
  isAutoTopUpPendingEnable: boolean
  isAutoTopUpLoading: boolean
  handleTopUp: (amount: number) => void
  handleRedeemCode: (code: string) => void
  handleAddPaymentMethod: () => void
  handleAutoTopUpToggle: (isEnabled: boolean) => void
  handleAutoTopUpSave: (data: { threshold: number; topUpAmount: number }) => void
  isFreePlan: boolean
  isSubscriptionModalOpen: boolean
  setIsSubscriptionModalOpen: (isOpen: boolean) => void
  planList: BillingPlan[]
  handleSelectPlan: (plan: BillingPlan, billingInterval: BillingInterval) => void
  isSubscribing: boolean
  handleTopUpClick: () => void
}

const useModalState = (
  isFreePlan: boolean,
  isEarlyAccess: boolean,
  topUp: (amount: number) => Promise<boolean>,
  subscribe: (planName: string, isYearly: boolean) => Promise<boolean>
) => {
  const [isTopUpModalOpen, setIsTopUpModalOpen] = useState(false)
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false)

  const handleTopUp = useCallback(
    async (amount: number): Promise<void> => {
      const success = await topUp(amount)
      if (success) setIsTopUpModalOpen(false)
    },
    [topUp]
  )

  const handleSelectPlan = useCallback(
    async (plan: BillingPlan, billingInterval: BillingInterval): Promise<void> => {
      const success = await subscribe(plan.name, billingInterval === 'yearly')
      if (success) setIsSubscriptionModalOpen(false)
    },
    [subscribe]
  )

  // Early Access tenants are on the Free plan in billing but are granted
  // Pro-level capabilities including top-up — skip the "subscribe first"
  // branch for them.
  const handleTopUpClick = useCallback((): void => {
    if (isFreePlan && !isEarlyAccess) {
      setIsSubscriptionModalOpen(true)
      return
    }
    setIsTopUpModalOpen(true)
  }, [isFreePlan, isEarlyAccess])

  return {
    isTopUpModalOpen,
    setIsTopUpModalOpen,
    isSubscriptionModalOpen,
    setIsSubscriptionModalOpen,
    handleTopUp,
    handleSelectPlan,
    handleTopUpClick,
  }
}

const buildBalanceSettingsReturn = (params: {
  tenant: ReturnType<typeof selectTenant>
  modals: ReturnType<typeof useModalState>
  autoTopUp: ReturnType<typeof useAutoTopUpState>
  isTopUpLoading: boolean
  isRedeemLoading: boolean
  isAddingPaymentMethod: boolean
  isFreePlan: boolean
  planList: BillingPlan[]
  isSubscribing: boolean
  handleRedeemCode: (code: string) => void
  handleAddPaymentMethod: () => Promise<void>
}): UseBalanceSettingsReturn => ({
  balance: deriveBalance(params.tenant),
  hasPaymentMethod: params.tenant?.defaultPaymentMethod !== null,
  isTopUpModalOpen: params.modals.isTopUpModalOpen,
  setIsTopUpModalOpen: params.modals.setIsTopUpModalOpen,
  isTopUpLoading: params.isTopUpLoading,
  isRedeemLoading: params.isRedeemLoading,
  isAddingPaymentMethod: params.isAddingPaymentMethod,
  autoTopUpSettings: params.autoTopUp.autoTopUpSettings,
  isAutoTopUpPendingEnable: params.autoTopUp.isPendingEnable,
  isAutoTopUpLoading: params.autoTopUp.isLoading,
  handleTopUp: params.modals.handleTopUp,
  handleRedeemCode: params.handleRedeemCode,
  handleAddPaymentMethod: params.handleAddPaymentMethod,
  handleAutoTopUpToggle: params.autoTopUp.handleAutoTopUpToggle,
  handleAutoTopUpSave: params.autoTopUp.handleAutoTopUpSave,
  isFreePlan: params.isFreePlan,
  isSubscriptionModalOpen: params.modals.isSubscriptionModalOpen,
  setIsSubscriptionModalOpen: params.modals.setIsSubscriptionModalOpen,
  planList: params.planList,
  handleSelectPlan: params.modals.handleSelectPlan,
  isSubscribing: params.isSubscribing,
  handleTopUpClick: params.modals.handleTopUpClick,
})

export const useBalanceSettings = (
  onClientSecret: (clientSecret: string) => void,
  onSubscriptionClientSecret: (clientSecret: string, planName: string, isYearly: boolean) => void
): UseBalanceSettingsReturn => {
  const tenant = useAppSelector(selectTenant)
  const { redeemCode, isLoading: isRedeemLoading } = useRedeemCode()
  const { topUp, isLoading: isTopUpLoading } = useTopUp()
  const {
    setupPaymentMethodCheckout,
    getAutoTopUpConfig,
    upsertAutoTopUpConfig,
    deleteAutoTopUpConfig,
    refetchBalance,
  } = useBillingClient()

  useEffect(() => void refetchBalance(), [refetchBalance])

  const { initiateCheckout, isLoading: isAddingPaymentMethod } = usePaymentMethodCheckout(
    setupPaymentMethodCheckout,
    onClientSecret
  )
  const { isFreePlan } = useSubscriptionState()
  const { isEarlyAccess } = useOrganizationEarlyAccess()
  const { planList, subscribe, isSubscribing } = useSubscription(onSubscriptionClientSecret)
  const autoTopUp = useAutoTopUpState({
    getConfig: getAutoTopUpConfig,
    upsertConfig: upsertAutoTopUpConfig,
    deleteConfig: deleteAutoTopUpConfig,
  })
  const modals = useModalState(isFreePlan, isEarlyAccess, topUp, subscribe)
  const handleRedeemCode = useCallback((code: string): void => void redeemCode(code), [redeemCode])

  return buildBalanceSettingsReturn({
    tenant,
    modals,
    autoTopUp,
    isTopUpLoading,
    isRedeemLoading,
    isAddingPaymentMethod,
    isFreePlan,
    planList,
    isSubscribing,
    handleRedeemCode,
    handleAddPaymentMethod: initiateCheckout,
  })
}
